import React, { useEffect, useRef, useCallback, forwardRef } from 'react';

import {
  Box,
  Icon,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  FormLabel,
  useNumberInput,
  useMergeRefs,
} from '@chakra-ui/react';

import { X } from 'react-feather';

import { ECustomTipsMode } from '@olo-web/domain/orders/types';
import { useOrderTipsDispatch } from '@olo-web/client-state';
import { useCurrentOrderTips } from '@olo-web/utils/common/hooks';
import { useRouter } from 'next/router';

const isDecimalWithTwoDigits = (value: string) => /^\d+\.?\d{0,2}$/.test(value);
const isIntegerFromOneToHundred = (value: string) => /^[1-9][0-9]?$|^100$/.test(value);

export const CustomTipsInput = forwardRef<HTMLInputElement>((_, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { merchantId } = router.query;

  const orderTipsDispatch = useOrderTipsDispatch();

  const { customTipsMode, customTipsAmount, customTipsPercentage } = useCurrentOrderTips();

  const handleFocusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    handleFocusInput();
  }, [customTipsMode, handleFocusInput]);

  const isAmountMode = customTipsMode === ECustomTipsMode.AMOUNT;

  const { getInputProps } = useNumberInput({
    ...(isAmountMode
      ? {
          min: 0,
          precision: 2,
          inputMode: 'decimal',
          value: customTipsAmount,
          onChange: (numericString) => {
            if (!numericString || isDecimalWithTwoDigits(numericString)) {
              orderTipsDispatch({
                type: 'TIPS_AMOUNT_CHANGE',
                payload: {
                  merchantId: merchantId as string,
                  value: numericString,
                },
              });
            }
          },
        }
      : {
          min: 1,
          max: 100,
          precision: 0,
          inputMode: 'numeric',
          value: customTipsPercentage,
          onChange: (numericString) => {
            if (!numericString || isIntegerFromOneToHundred(numericString)) {
              orderTipsDispatch({
                type: 'TIPS_PERCENTAGE_CHANGE',
                payload: {
                  merchantId: merchantId as string,
                  value: numericString,
                },
              });
            }
          },
        }),
  });

  const inputProps = getInputProps();
  const refs = useMergeRefs(inputRef, ref);

  return (
    <>
      <FormLabel
        htmlFor="custom-tip-input"
        fontSize="0.9rem"
        fontWeight="normal"
        color="blackAlpha.600"
        mb={2}
      >
        {isAmountMode ? 'Custom tip amount' : 'Custom tip percentage'}
      </FormLabel>
      <InputGroup>
        {isAmountMode && (
          <InputLeftElement
            w="auto"
            h="100%"
            pl="16px"
            pr="4px"
            textStyle="p"
            data-testid="tip-type-dollar-sign"
            aria-hidden="true"
          >
            $
          </InputLeftElement>
        )}

        <Input
          {...inputProps}
          id="custom-tip-input"
          aria-label={`Custom tips input in ${isAmountMode ? 'dollars' : 'percentage'}`}
          ref={refs}
          textStyle="p"
          m="1.5px"
          data-testid="tip-input"
          _placeholder={{ color: 'blackAlpha.350' }}
          borderColor="black"
          _hover={{ borderColor: 'black' }}
          _focus={{
            borderColor: 'primary.400',
          }}
          {...(isAmountMode
            ? {
                pl: '28px',
                pr: '35px',
              }
            : {
                textAlign: 'right',
                pl: '16px',
                pr: '72px',
              })}
        />

        <InputRightElement w="auto" h="100%" pl="4px" textStyle="p">
          {!isAmountMode && (
            <Box mr="10px" data-testid="tip-type-percent-sign">
              %
            </Box>
          )}

          <IconButton
            aria-label="Clear custom tip amount or percentage"
            variant="ghost"
            colorScheme="blackAlpha"
            data-testid="clear-tip-input"
            icon={<Icon as={X} h={5} w={5} />}
            onClick={() => {
              if (isAmountMode) {
                orderTipsDispatch({
                  type: 'TIPS_AMOUNT_CHANGE',
                  payload: {
                    merchantId: merchantId as string,
                    value: '',
                  },
                });
              } else {
                orderTipsDispatch({
                  type: 'TIPS_PERCENTAGE_CHANGE',
                  payload: {
                    merchantId: merchantId as string,
                    value: '',
                  },
                });
              }

              handleFocusInput();
            }}
          />
        </InputRightElement>
      </InputGroup>
    </>
  );
});
